import { useEffect, type ReactNode } from "react";
import { CircleHelp, TriangleAlert } from "lucide-react";
import { Button } from "@/shared/components/Button";

interface ModalConfirmacaoProps {
  aberto: boolean;
  titulo: string;
  mensagem: string;
  textoBotaoConfirmar?: string;
  /** Rótulo do botão de recuo (padrão "Voltar"). */
  textoBotaoCancelar?: string;
  carregando?: boolean;
  /**
   * "perigo" (padrão, vermelho) para ações destrutivas — excluir, rejeitar.
   * "padrao" (verde, cor da marca) para ações positivas — solicitar,
   * aprovar, devolver.
   */
  variante?: "perigo" | "padrao";
  /** Mensagem de erro da última tentativa (ex.: falha ao excluir/cancelar). */
  erro?: string | null;
  /** Conteúdo extra entre a mensagem e os botões (ex.: campo de motivo). */
  children?: ReactNode;
  /** Desabilita o botão de confirmar (ex.: campo obrigatório ainda vazio). */
  confirmarDesabilitado?: boolean;
  onConfirmar: () => void;
  onCancelar: () => void;
}

/**
 * Modal de confirmação genérico para o fluxo de empréstimos e exclusões.
 * Impede ações acidentais, especialmente em mobile onde toques
 * involuntários são mais comuns.
 */
export function ModalConfirmacao({
  aberto,
  titulo,
  mensagem,
  textoBotaoConfirmar = "Excluir",
  textoBotaoCancelar = "Voltar",
  carregando = false,
  variante = "perigo",
  erro,
  children,
  confirmarDesabilitado = false,
  onConfirmar,
  onCancelar,
}: ModalConfirmacaoProps) {
  // Esc cancela, exceto enquanto a ação está em andamento.
  useEffect(() => {
    if (!aberto || carregando) return;
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === "Escape") onCancelar();
    }
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [aberto, carregando, onCancelar]);

  if (!aberto) return null;

  const ehPerigo = variante === "perigo";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirmacao-titulo"
      onClick={carregando ? undefined : onCancelar}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-border bg-surface p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <div className={`flex h-12 w-12 items-center justify-center rounded-full ${ehPerigo ? "bg-danger/10" : "bg-primary/10"}`}>
            {ehPerigo ? (
              <TriangleAlert size={24} className="text-danger" />
            ) : (
              <CircleHelp size={24} className="text-primary" />
            )}
          </div>
          <h2
            id="confirmacao-titulo"
            className="text-base font-semibold text-text"
          >
            {titulo}
          </h2>
          <p className="text-sm leading-relaxed text-muted">{mensagem}</p>
          {erro && (
            <p role="alert" className="w-full rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
              {erro}
            </p>
          )}
        </div>

        {children && <div className="mt-4 text-left">{children}</div>}

        <div className="mt-6 flex flex-col gap-2 sm:flex-row-reverse">
          <Button
            onClick={onConfirmar}
            carregando={carregando}
            disabled={confirmarDesabilitado}
            className={`flex-1 ${ehPerigo ? "!bg-danger text-white hover:!bg-danger/90" : "bg-primary text-on-primary hover:bg-primary-hover"}`}
          >
            {textoBotaoConfirmar}
          </Button>
          <Button
            onClick={onCancelar}
            disabled={carregando}
            className="flex-1 bg-surface-2 text-text hover:bg-border"
          >
            {textoBotaoCancelar}
          </Button>
        </div>
      </div>
    </div>
  );
}
